import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, apiUrl } from '../api/client.js';
import { useConnections, usePersonalBot, usePrefs } from '../api/hooks.js';
import type { Connection, PersonalBot, ToolCounts } from '../api/types.js';
import { Badge, Card, ConfirmDialog, Modal, PageTitle, SectionLabel } from '../components/ui.js';
import { PersonaWizard } from '../components/PersonaWizard.js';
import { Button } from '../components/ui/Button.js';
import { Input, Select } from '../components/ui/Input.js';
import { getSessionToken } from '../lib/auth.js';

const CONNECTOR_LABELS: Record<string, string> = {
  ndoc: 'NDoc',
  kanban: 'Kanban',
  memory: 'Memory',
  mattermost: 'Mattermost',
  github: 'GitHub',
  notion: 'Notion',
  orbit: 'Orbit',
  hbk: 'HBK',
  fastmail: 'Fastmail',
  ndrive: 'NDrive',
};

type Access = 'off' | 'read-only' | 'full';

export function Connections() {
  const queryClient = useQueryClient();
  const connections = useConnections();
  const prefs = usePrefs();
  const bot = usePersonalBot();
  const counts = useQuery({
    queryKey: ['tool-counts'],
    queryFn: () => api.get<ToolCounts>('/api/connections/tool-counts'),
  });
  const [addSharedOpen, setAddSharedOpen] = useState(false);
  const [deleteName, setDeleteName] = useState<string | null>(null);
  const [removeSharedName, setRemoveSharedName] = useState<string | null>(null);
  const [editShared, setEditShared] = useState<Connection | null>(null);
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});

  useEffect(() => {
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
    };
    window.addEventListener('loom-connections-changed', refresh);
    const onVis = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('loom-connections-changed', refresh);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [queryClient]);

  const company = (connections.data ?? []).filter((c) => !c.personal && !c.sharedCatalog);
  const shared = (connections.data ?? []).filter((c) => c.sharedCatalog);
  const personal = (connections.data ?? []).filter((c) => c.personal);
  /** Bot tool access rows: only connectors this user has Connected (company + shared). */
  const connectedBotTools = (connections.data ?? [])
    .filter((c) => !c.personal)
    .filter(
      (c) =>
        c.userConnected ||
        c.userApiKeySet ||
        (c.sharedCatalog === true && c.authType === 'none'),
    )
    .map((c) => c.name);

  async function connect(name: string) {
    const r = await api.post<{ authUrl?: string; alreadyConnected?: boolean }>(
      `/api/connections/${encodeURIComponent(name)}/start`,
    );
    if (r.alreadyConnected) {
      toast.success('Already connected');
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      return;
    }
    if (r.authUrl) window.open(r.authUrl, 'loom-oauth', 'width=520,height=680');
  }

  async function disconnect(name: string) {
    await api.delete(`/api/connections/${encodeURIComponent(name)}`);
    toast.success('Disconnected');
    void queryClient.invalidateQueries({ queryKey: ['connections'] });
  }

  async function disconnectShared(name: string) {
    await api.delete(`/api/shared-catalog/${encodeURIComponent(name)}/connection`);
    toast.success('Disconnected');
    void queryClient.invalidateQueries({ queryKey: ['connections'] });
    void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
  }

  async function saveKey(name: string) {
    const key = apiKeys[name]?.trim();
    if (!key) return;
    await api.post(`/api/connections/${encodeURIComponent(name)}/apikey`, { key });
    toast.success('Key saved');
    setApiKeys((s) => ({ ...s, [name]: '' }));
    void queryClient.invalidateQueries({ queryKey: ['connections'] });
    void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
  }

  async function saveSharedKey(name: string) {
    const token = apiKeys[name]?.trim();
    if (!token) return;
    await api.post(`/api/shared-catalog/${encodeURIComponent(name)}/connect`, { token });
    toast.success('Connected');
    setApiKeys((s) => ({ ...s, [name]: '' }));
    void queryClient.invalidateQueries({ queryKey: ['connections'] });
    void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
  }

  async function toggle(name: string, currentlyEnabled: boolean) {
    const disabled = new Set(prefs.data?.disabledConnectors ?? []);
    if (currentlyEnabled) disabled.add(name);
    else disabled.delete(name);
    await api.put('/api/prefs', { disabledConnectors: [...disabled] });
    void queryClient.invalidateQueries({ queryKey: ['connections'] });
    void queryClient.invalidateQueries({ queryKey: ['prefs'] });
    void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
  }

  async function toggleToolHide(toolName: string, isCurrentlyHidden: boolean) {
    const disabled = new Set(prefs.data?.disabledTools ?? []);
    if (isCurrentlyHidden) disabled.delete(toolName);
    else disabled.add(toolName);
    await api.put('/api/prefs', { disabledTools: [...disabled] });
    toast.success(isCurrentlyHidden ? 'Tool restored' : 'Tool hidden from MCP');
    void queryClient.invalidateQueries({ queryKey: ['prefs'] });
    void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
  }

  return (
    <div>
      <PageTitle
        title="My Connections"
        sub="Company tools, team-shared MCP servers, and your connections."
      />

      <div className="mb-2 flex items-center justify-between">
        <SectionLabel>Company tools</SectionLabel>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void queryClient
              .invalidateQueries({ queryKey: ['tool-counts'] })
              .then(() => toast.success('Tool counts refreshed'))
              .catch((err: Error) => toast.error(err.message));
          }}
        >
          Refresh counts
        </Button>
      </div>
      <div className="space-y-2">
        {company.map((c) => (
          <ConnectorCard
            key={c.name}
            c={c}
            count={toolCount(counts.data, c.name)}
            tools={toolList(counts.data, c.name)}
            failed={counts.data?._failed?.includes(c.name) ?? false}
            apiKey={apiKeys[c.name] ?? ''}
            onApiKey={(v) => setApiKeys((s) => ({ ...s, [c.name]: v }))}
            onConnect={() => void connect(c.name).catch((err: Error) => toast.error(err.message))}
            onDisconnect={() => void disconnect(c.name).catch((err: Error) => toast.error(err.message))}
            onSaveKey={() => void saveKey(c.name).catch((err: Error) => toast.error(err.message))}
            onToggle={() => void toggle(c.name, c.userEnabled).catch((err: Error) => toast.error(err.message))}
            onToggleTool={(tool, hidden) =>
              void toggleToolHide(tool, hidden).catch((err: Error) => toast.error(err.message))
            }
          />
        ))}
      </div>

      <SectionLabel>Shared tools</SectionLabel>
      <div className="space-y-2">
        {shared.map((c) => (
          <ConnectorCard
            key={c.name}
            c={c}
            count={toolCount(counts.data, c.name)}
            tools={toolList(counts.data, c.name)}
            failed={counts.data?._failed?.includes(c.name) ?? false}
            apiKey={apiKeys[c.name] ?? ''}
            onApiKey={(v) => setApiKeys((s) => ({ ...s, [c.name]: v }))}
            onConnect={() => startSharedOAuth(c.name)}
            onDisconnect={() =>
              void disconnectShared(c.name).catch((err: Error) => toast.error(err.message))
            }
            onSaveKey={() => void saveSharedKey(c.name).catch((err: Error) => toast.error(err.message))}
            onToggle={() => void toggle(c.name, c.userEnabled).catch((err: Error) => toast.error(err.message))}
            onToggleTool={(tool, hidden) =>
              void toggleToolHide(tool, hidden).catch((err: Error) => toast.error(err.message))
            }
            onEditShared={c.canManage ? () => setEditShared(c) : undefined}
            onRemoveShared={c.canManage ? () => setRemoveSharedName(c.name) : undefined}
          />
        ))}
      </div>
      <div className="mt-3">
        <Button variant="secondary" size="sm" onClick={() => setAddSharedOpen(true)}>
          Add tool
        </Button>
      </div>

      {personal.length > 0 ? (
        <>
          <SectionLabel>Legacy personal tools</SectionLabel>
          <p className="mb-2 text-xs text-[var(--color-ink-faint)]">
            Older private-only tools. New tools use Add tool above and appear for everyone.
          </p>
          <div className="space-y-2">
            {personal.map((c) => (
              <ConnectorCard
                key={c.name}
                c={c}
                count={toolCount(counts.data, c.name)}
                tools={toolList(counts.data, c.name)}
                failed={counts.data?._failed?.includes(c.name) ?? false}
                apiKey=""
                onApiKey={() => {}}
                onConnect={() => startPersonalOAuth(c.name, c.url)}
                onDisconnect={() => setDeleteName(c.name)}
                onSaveKey={() => {}}
                onToggle={() =>
                  void toggle(c.name, c.userEnabled).catch((err: Error) => toast.error(err.message))
                }
                onToggleTool={(tool, hidden) =>
                  void toggleToolHide(tool, hidden).catch((err: Error) => toast.error(err.message))
                }
                personal
              />
            ))}
          </div>
        </>
      ) : null}

      <SectionLabel>Personal bot</SectionLabel>
      <PersonalBotPanel bot={bot.data} connectedNames={connectedBotTools} />

      {addSharedOpen ? <AddSharedToolModal onClose={() => setAddSharedOpen(false)} /> : null}
      {editShared ? (
        <EditSharedToolModal connection={editShared} onClose={() => setEditShared(null)} />
      ) : null}
      {deleteName ? (
        <ConfirmDialog
          title={`Delete “${deleteName}”?`}
          body="This personal tool will be permanently removed."
          warn="This cannot be undone."
          confirmText="Delete tool"
          onClose={() => setDeleteName(null)}
          onConfirm={() => {
            const name = deleteName;
            setDeleteName(null);
            void api.delete(`/api/my-connectors/${encodeURIComponent(name)}`).then(() => {
              toast.success('Deleted');
              void queryClient.invalidateQueries({ queryKey: ['connections'] });
            });
          }}
        />
      ) : null}
      {removeSharedName ? (
        <ConfirmDialog
          title={`Remove “${removeSharedName}”?`}
          body="This shared tool will be removed for everyone."
          warn="Clears everyone’s credentials for this tool. This cannot be undone."
          confirmText="Remove shared tool"
          onClose={() => setRemoveSharedName(null)}
          onConfirm={() => {
            const name = removeSharedName;
            setRemoveSharedName(null);
            void api
              .delete(`/api/shared-catalog/${encodeURIComponent(name)}`)
              .then(() => {
                toast.success('Removed');
                void queryClient.invalidateQueries({ queryKey: ['connections'] });
                void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
              })
              .catch((err: Error) => toast.error(err.message));
          }}
        />
      ) : null}
    </div>
  );
}

function toolCount(data: ToolCounts | undefined, name: string): number | null {
  const d = data?.[name];
  return typeof d === 'object' && d && 'count' in d ? d.count : null;
}

function toolList(
  data: ToolCounts | undefined,
  name: string,
): { name: string; desc: string; hidden?: boolean }[] {
  const d = data?.[name];
  if (!d || typeof d !== 'object' || !('tools' in d)) return [];
  const tools = d.tools;
  return Array.isArray(tools) ? tools : [];
}

function ConnectorCard({
  c,
  count,
  tools,
  failed,
  apiKey,
  onApiKey,
  onConnect,
  onDisconnect,
  onSaveKey,
  onToggle,
  onToggleTool,
  personal,
  onEditShared,
  onRemoveShared,
}: {
  c: Connection;
  count: number | null;
  tools: { name: string; desc: string; hidden?: boolean }[];
  failed: boolean;
  apiKey: string;
  onApiKey: (v: string) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onSaveKey: () => void;
  onToggle: () => void;
  onToggleTool: (toolName: string, isHidden: boolean) => void;
  personal?: boolean;
  onEditShared?: () => void;
  onRemoveShared?: () => void;
}) {
  const sharedCatalog = !!c.sharedCatalog;
  const connected = c.userConnected || c.userApiKeySet || c.shared;
  const showConnect = c.supportsPerUserAuth && !c.userConnected;
  const showDisconnect = sharedCatalog
    ? c.userConnected || c.userApiKeySet
    : c.supportsPerUserAuth && c.userConnected;
  return (
    <Card className={`p-4 ${c.userEnabled ? '' : 'opacity-50'}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {c.name}
            {personal ? (
              <span className="ml-2 text-[10px] font-medium text-[var(--color-ink-faint)]">PERSONAL</span>
            ) : null}
            {sharedCatalog ? (
              <span className="ml-2 text-[10px] font-medium text-[var(--color-ink-faint)]">SHARED</span>
            ) : null}
          </div>
          <div className="truncate font-mono text-[11px] text-[var(--color-ink-faint)]">{c.url}</div>
          {sharedCatalog ? (
            <div className="mt-0.5 text-[11px] text-[var(--color-ink-faint)]">
              Added by {c.addedByName ?? 'member'}
            </div>
          ) : null}
          <div className="mt-1 flex flex-wrap gap-1">
            <Badge tone={connected ? 'good' : 'warn'}>{connected ? 'Connected' : 'Not connected'}</Badge>
            <Badge>{c.authType}</Badge>
            <span className="text-[11px] text-[var(--color-ink-faint)]">
              {failed ? '? tools' : count !== null ? `${count} tools` : ''}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {showConnect ? (
            <Button size="sm" onClick={onConnect}>
              Connect
            </Button>
          ) : null}
          {showDisconnect ? (
            <Button variant="danger" size="sm" onClick={onDisconnect}>
              Disconnect
            </Button>
          ) : null}
          {personal ? (
            <>
              {c.authType === 'oauth' && !c.userConnected ? (
                <Button size="sm" onClick={onConnect}>
                  Authorize
                </Button>
              ) : null}
              <Button variant="danger" size="sm" onClick={onDisconnect}>
                Delete
              </Button>
            </>
          ) : null}
          {onEditShared ? (
            <Button variant="ghost" size="sm" onClick={onEditShared}>
              Edit
            </Button>
          ) : null}
          {onRemoveShared ? (
            <Button variant="danger" size="sm" onClick={onRemoveShared}>
              Remove
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onToggle}>
            {c.userEnabled ? 'Hide' : 'Show'}
          </Button>
        </div>
      </div>
      {tools.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1 border-t border-[var(--color-line)] pt-3">
          {tools.map((t) => (
            <button
              key={t.name}
              type="button"
              title={
                (t.hidden ? 'Click to restore' : 'Click to hide from MCP') +
                (t.desc ? ` — ${t.desc}` : '')
              }
              className={`rounded border px-2 py-0.5 font-mono text-[10px] transition-colors ${
                t.hidden
                  ? 'border-dashed border-[var(--color-line)] text-[var(--color-ink-faint)] opacity-60'
                  : 'border-[var(--color-line)] text-[var(--color-ink-muted)] hover:border-[var(--color-accent)]'
              }`}
              onClick={() => onToggleTool(t.name, !!t.hidden)}
            >
              {t.hidden ? '↩ ' : ''}
              {t.name}
            </button>
          ))}
        </div>
      ) : null}
      {c.supportsUserApiKey ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--color-line)] pt-3">
          <span className="text-xs text-[var(--color-ink-muted)]">
            {sharedCatalog ? 'Your token' : 'Personal API key'}
          </span>
          <Input
            type="password"
            className="max-w-xs"
            placeholder={c.userApiKeySet ? 'Key saved — paste to replace' : 'Paste your API key…'}
            value={apiKey}
            onChange={(e) => onApiKey(e.target.value)}
          />
          <Button size="sm" onClick={onSaveKey} disabled={!apiKey.trim()}>
            {sharedCatalog ? 'Connect' : 'Save'}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function startPersonalOAuth(name: string, url: string) {
  const jwt = getSessionToken() || '';
  const params = new URLSearchParams({ name, url, jwt });
  const popup = window.open(
    apiUrl(`/api/my-connectors/oauth/start?${params}`),
    'loom-personal-oauth',
    'width=520,height=680,resizable=yes,scrollbars=yes',
  );
  const allowed = (() => {
    try {
      return new URL(apiUrl('/')).origin;
    } catch {
      return window.location.origin;
    }
  })();
  const handler = (event: MessageEvent) => {
    if (event.origin !== allowed && event.origin !== window.location.origin) return;
    if (!event.data || event.data.type !== 'loom-oauth-result') return;
    window.removeEventListener('message', handler);
    if (event.data.success) {
      toast.success(`“${name}” authorized`);
      window.dispatchEvent(new Event('loom-connections-changed'));
    } else toast.error(event.data.message ?? 'Authorization failed');
  };
  window.addEventListener('message', handler);
  const poll = setInterval(() => {
    if (popup && popup.closed) {
      clearInterval(poll);
      window.removeEventListener('message', handler);
    }
  }, 600);
}

function startSharedOAuth(name: string) {
  const jwt = getSessionToken() || '';
  const params = new URLSearchParams({ name, jwt });
  const popup = window.open(
    apiUrl(`/api/shared-catalog/oauth/start?${params}`),
    'loom-shared-oauth',
    'width=520,height=680,resizable=yes,scrollbars=yes',
  );
  const allowed = (() => {
    try {
      return new URL(apiUrl('/')).origin;
    } catch {
      return window.location.origin;
    }
  })();
  const handler = (event: MessageEvent) => {
    if (event.origin !== allowed && event.origin !== window.location.origin) return;
    if (!event.data || event.data.type !== 'loom-oauth-result') return;
    window.removeEventListener('message', handler);
    if (event.data.success) {
      toast.success(`“${name}” authorized`);
      window.dispatchEvent(new Event('loom-connections-changed'));
    } else toast.error(event.data.message ?? 'Authorization failed');
  };
  window.addEventListener('message', handler);
  const poll = setInterval(() => {
    if (popup && popup.closed) {
      clearInterval(poll);
      window.removeEventListener('message', handler);
    }
  }, 600);
}

function AddSharedToolModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [authType, setAuthType] = useState('none');
  const [headerName, setHeaderName] = useState('x-api-key');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!url.startsWith('http')) return;
    const t = setTimeout(() => {
      void api
        .get<{ supportsOAuth?: boolean }>(`/api/my-connectors/oauth/probe?url=${encodeURIComponent(url)}`)
        .then((r) => {
          if (r.supportsOAuth) setAuthType('oauth');
        })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [url]);

  async function submit() {
    setBusy(true);
    try {
      await api.post('/api/shared-catalog', {
        name,
        url,
        authType,
        headerName: authType === 'apiKey' ? headerName : undefined,
      });
      toast.success('Added — visible to everyone under Shared tools');
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add MCP tool"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !name || !url}>
            Add tool
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-[var(--color-ink-muted)]">
          Goes into the team shared catalog — everyone sees it on Connections. Each member Connects
          with their own credentials. This form only sets name, URL, and auth type — never secrets.
        </p>
        <label className="block text-xs font-medium text-[var(--color-ink-muted)]">Name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
          placeholder="clickup"
        />
        <label className="block text-xs font-medium text-[var(--color-ink-muted)]">MCP URL</label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://mcp.example.com/mcp" />
        <label className="block text-xs font-medium text-[var(--color-ink-muted)]">Auth</label>
        <Select value={authType} onChange={(e) => setAuthType(e.target.value)}>
          <option value="none">No auth</option>
          <option value="bearer">Bearer token</option>
          <option value="apiKey">API key</option>
          <option value="oauth">OAuth</option>
        </Select>
        {authType === 'apiKey' ? (
          <>
            <label className="block text-xs font-medium text-[var(--color-ink-muted)]">
              Header name (not the secret)
            </label>
            <Input
              value={headerName}
              onChange={(e) => setHeaderName(e.target.value)}
              placeholder="x-api-key"
            />
            <p className="text-[11px] text-[var(--color-ink-faint)]">
              Which HTTP header carries the key (e.g. <code className="font-mono">x-api-key</code> or{' '}
              <code className="font-mono">Authorization</code>). Do not put the API key value here.
            </p>
          </>
        ) : null}
        {authType === 'bearer' || authType === 'apiKey' ? (
          <p className="text-xs text-[var(--color-ink-muted)]">
            Secrets stay off this form. After you add the tool, each member opens the card and pastes
            their own key under <span className="font-medium">Your token</span>, then Connect.
          </p>
        ) : null}
        {authType === 'oauth' ? (
          <p className="text-xs text-[var(--color-ink-muted)]">
            After adding, each member clicks Connect and completes OAuth in the popup.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

function EditSharedToolModal({
  connection,
  onClose,
}: {
  connection: Connection;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(connection.url);
  const [authType, setAuthType] = useState(connection.authType);
  const [headerName, setHeaderName] = useState(connection.headerName ?? 'x-api-key');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      const body: { url: string; authType: string; headerName?: string } = { url, authType };
      if (authType === 'apiKey') body.headerName = headerName;
      await api.patch(`/api/shared-catalog/${encodeURIComponent(connection.name)}`, body);
      toast.success('Updated — all members must re-Connect');
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      void queryClient.invalidateQueries({ queryKey: ['tool-counts'] });
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Edit “${connection.name}”`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !url}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-[var(--color-ink-muted)]">
          Saving clears everyone’s credentials for this tool. All members must re-Connect.
        </p>
        <label className="block text-xs font-medium text-[var(--color-ink-muted)]">MCP URL</label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} />
        <label className="block text-xs font-medium text-[var(--color-ink-muted)]">Auth</label>
        <Select value={authType} onChange={(e) => setAuthType(e.target.value)}>
          <option value="none">No auth</option>
          <option value="bearer">Bearer token</option>
          <option value="apiKey">API key</option>
          <option value="oauth">OAuth</option>
        </Select>
        {authType === 'apiKey' ? (
          <>
            <label className="block text-xs font-medium text-[var(--color-ink-muted)]">
              Header name (not the secret)
            </label>
            <Input
              value={headerName}
              onChange={(e) => setHeaderName(e.target.value)}
              placeholder="x-api-key"
            />
            <p className="text-[11px] text-[var(--color-ink-faint)]">
              Which HTTP header carries the key — not the API key value itself.
            </p>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

function TokenStatusBadge({
  saved,
  status,
}: {
  saved: boolean;
  status?: 'ok' | 'missing' | 'expired' | 'expiring';
}) {
  if (!saved || status === 'missing') {
    return <Badge tone="warn">Not saved</Badge>;
  }
  if (status === 'expired') {
    return <Badge tone="bad">Expired</Badge>;
  }
  if (status === 'expiring') {
    return <Badge tone="warn">Expiring soon</Badge>;
  }
  return <Badge tone="good">Saved</Badge>;
}

function PersonalBotPanel({
  bot,
  connectedNames,
}: {
  bot?: PersonalBot;
  connectedNames: string[];
}) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const [creating, setCreating] = useState(false);
  const [perms, setPerms] = useState<Record<string, Access>>({});
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [ownerSaveBusy, setOwnerSaveBusy] = useState(false);
  const [botLoomToken, setBotLoomToken] = useState('');
  const [ownerLoomToken, setOwnerLoomToken] = useState('');

  useEffect(() => {
    if (!bot?.exists) return;
    const next: Record<string, Access> = {};
    for (const c of bot.allowedConnectors ?? []) next[c.name] = c.access;
    setPerms(next);
  }, [bot]);

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy — select the token and copy manually');
    }
  }

  if (!bot?.exists) {
    return (
      <Card className="p-4">
        <p className="mb-3 text-sm text-[var(--color-ink-muted)]">
          Paste your Mattermost bot token. Loom resolves the username, issues the Loom MCP token for
          the runtime, and builds a draft persona from your Kanban, NDoc, Mattermost, and Memory data — you
          can edit and push it after create.
        </p>
        <p className="mb-3 text-xs text-[var(--color-ink-faint)]">
          Connect at least one of Kanban / NDoc / Mattermost / Memory above first.
        </p>
        <div className="max-w-md space-y-2">
          <Input
            type="password"
            placeholder="Mattermost bot token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={creating}
          />
          <Button
            size="sm"
            disabled={!token.trim() || creating}
            onClick={() => {
              setCreating(true);
              void api
                .post<{ botUsername?: string }>('/api/user/personal-bot', { botToken: token.trim() })
                .then((r) => {
                  toast.success(
                    r.botUsername
                      ? `Personal bot @${r.botUsername} created — review persona below`
                      : 'Personal bot created — review persona below',
                  );
                  setToken('');
                  void queryClient.invalidateQueries({ queryKey: ['personal-bot'] });
                })
                .catch((err: Error) => toast.error(err.message))
                .finally(() => setCreating(false));
            }}
          >
            {creating ? 'Creating…' : 'Create personal bot'}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">@{bot.botUsername}</div>
          <div className="text-xs text-[var(--color-ink-faint)]">
            Mattermost token: {bot.botTokenMasked}
            {bot.runtime ? ` · runtime ${bot.runtime}` : ''}
          </div>
        </div>
        <Button variant="danger" size="sm" onClick={() => setConfirmRemove(true)}>
          Remove bot
        </Button>
      </div>

      <PersonaWizard persona={bot.persona} />

      <div className="mb-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[11px] font-medium text-[var(--color-ink-muted)]">
            Loom tokens
          </div>
          <Badge tone={bot.roleMode === 'pa' ? 'good' : undefined}>
            {bot.roleMode === 'pa' ? 'Mode: PA' : 'Mode: Teammate'}
          </Badge>
        </div>
        <p className="text-[11px] text-[var(--color-ink-faint)]">
          <strong>Teammate</strong> = Bot token only.{' '}
          <strong>PA</strong> = Bot + Owner; runtime picks which token from the user’s request.
          Paste a new token and Save &amp; push to <strong>replace</strong> (Loomy does not allow
          emptying tokens — bot token is always required). Mode updates after a successful Save.
        </p>
        {(bot.tokenWarnings?.length ?? 0) > 0 ? (
          <ul className="space-y-1 rounded-md border border-[var(--color-warn, #c47a00)]/40 bg-[var(--color-surface)] px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
            {bot.tokenWarnings!.map((w) => (
              <li key={w}>⚠ {w}</li>
            ))}
          </ul>
        ) : null}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium text-[var(--color-ink-muted)]">
              Bot Loom Token
            </span>
            <TokenStatusBadge
              saved={!!bot.hasBotLoomToken || !!bot.loomToken?.saved}
              status={bot.loomToken?.status}
            />
          </div>
          <p className="text-[11px] text-[var(--color-ink-faint)]">
            Log into Loom as the bot’s SSO account, generate an AI token, paste, then Save &amp; push.
          </p>
          <textarea
            className="w-full resize-y rounded border border-[var(--color-line)] bg-[var(--color-surface)] p-2 font-mono text-[10px] text-[var(--color-ink)]"
            rows={3}
            placeholder="Paste bot SSO Loom AI token…"
            value={botLoomToken}
            onChange={(e) => setBotLoomToken(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={saveBusy || !botLoomToken.trim()}
              onClick={() => {
                setSaveBusy(true);
                void api
                  .post('/api/user/personal-bot/loom-token', { loomToken: botLoomToken.trim() })
                  .then(() => {
                    toast.success('Bot token saved and pushed');
                    setBotLoomToken('');
                    void queryClient.invalidateQueries({ queryKey: ['personal-bot'] });
                  })
                  .catch((err: Error) => toast.error(err.message))
                  .finally(() => setSaveBusy(false));
              }}
            >
              {saveBusy ? 'Pushing…' : 'Save & push'}
            </Button>
            {botLoomToken.trim() ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void copyText(botLoomToken.trim())}
              >
                Copy
              </Button>
            ) : null}
          </div>
        </div>

        <div className="space-y-2 border-t border-[var(--color-line)] pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium text-[var(--color-ink-muted)]">
              Owner Loom Token (PA)
            </span>
            <TokenStatusBadge
              saved={!!bot.hasOwnerLoomToken || !!bot.ownerLoomToken?.saved}
              status={bot.ownerLoomToken?.status}
            />
          </div>
          <p className="text-[11px] text-[var(--color-ink-faint)]">
            Paste your own Loom AI token (AI Config while logged in as you). Required for PA. To
            change it later, paste a new token and Save &amp; push again (replace).
          </p>
          <textarea
            className="w-full resize-y rounded border border-[var(--color-line)] bg-[var(--color-surface)] p-2 font-mono text-[10px] text-[var(--color-ink)]"
            rows={3}
            placeholder="Paste your owner Loom AI token…"
            value={ownerLoomToken}
            onChange={(e) => setOwnerLoomToken(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={ownerSaveBusy || !ownerLoomToken.trim()}
              onClick={() => {
                setOwnerSaveBusy(true);
                void api
                  .post('/api/user/personal-bot/owner-loom-token', {
                    loomToken: ownerLoomToken.trim(),
                  })
                  .then(() => {
                    toast.success('Owner token saved — Mode: PA');
                    setOwnerLoomToken('');
                    void queryClient.invalidateQueries({ queryKey: ['personal-bot'] });
                  })
                  .catch((err: Error) => toast.error(err.message))
                  .finally(() => setOwnerSaveBusy(false));
              }}
            >
              {ownerSaveBusy ? 'Pushing…' : 'Save & push owner'}
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-1 text-[11px] font-medium text-[var(--color-ink-muted)]">
        Bot tool access (enforced by Loom when the bot calls MCP)
      </div>
      <p className="mb-2 text-xs text-[var(--color-ink-faint)]">
        Only tools you have Connected above. Off hides a connector. Read and Full both let the bot call every tool on it. Owner memory still follows Read versus Full, and memory deletes stay blocked.
      </p>
      <div className="space-y-1">
        {connectedNames.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-[var(--color-ink-muted)]">
            No connected tools yet.
          </p>
        ) : (
          connectedNames.map((key) => {
            const label = CONNECTOR_LABELS[key] ?? key;
            const current = perms[key] ?? 'off';
            return (
              <div key={key} className="flex items-center gap-2 rounded-md px-2 py-1.5">
                <span className="flex-1 text-sm">{label}</span>
                {(['off', 'read-only', 'full'] as const).map((level) => (
                  <Button
                    key={level}
                    size="sm"
                    variant={current === level ? 'primary' : 'ghost'}
                    onClick={() => setPerms((p) => ({ ...p, [key]: level }))}
                  >
                    {level === 'read-only' ? 'Read' : level === 'full' ? 'Full' : 'Off'}
                  </Button>
                ))}
              </div>
            );
          })
        )}
      </div>
      <Button
        className="mt-3"
        size="sm"
        disabled={connectedNames.length === 0}
        onClick={() =>
          void api
            .patch('/api/user/personal-bot/permissions', {
              allowedConnectors: connectedNames.map((name) => ({
                name,
                access: perms[name] ?? 'off',
              })),
            })
            .then(() => toast.success('Permissions saved — bot sessions refreshed'))
            .catch((err: Error) => toast.error(err.message))
        }
      >
        Save permissions
      </Button>

      {confirmRemove ? (
        <ConfirmDialog
          title="Remove personal bot?"
          body="The bot stops and is deleted from the runtime."
          confirmText="Remove"
          onClose={() => setConfirmRemove(false)}
          onConfirm={() => {
            setConfirmRemove(false);
            void api.delete('/api/user/personal-bot').then(() => {
              toast.success('Removed');
              void queryClient.invalidateQueries({ queryKey: ['personal-bot'] });
            });
          }}
        />
      ) : null}
    </Card>
  );
}
